import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // CRITICAL FIX: Implement session polling to handle race condition
        // After email confirmation, session may not be immediately available
        let session = null;
        let attempts = 0;
        const maxAttempts = 10;
        
        while (!session && attempts < maxAttempts) {
          const { data: { session: currentSession }, error: sessionError } = await supabase?.auth?.getSession();
          
          if (sessionError) {
            console.error('❌ Error getting session:', sessionError);
            setIsProcessing(false);
            navigate('/login-form', { replace: true });
            return;
          }
          
          if (currentSession?.user) {
            session = currentSession;
            break;
          }
          
          // Wait 200ms before retrying
          attempts++;
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }

        // No session after polling - redirect to login
        if (!session || !session?.user) {
          console.log('⚠️ No session found after polling, redirecting to login');
          setIsProcessing(false);
          navigate('/login-form', { replace: true });
          return;
        }

        console.log('✅ Session established for user:', session?.user?.email);

        // Read role and registration_step from the authoritative users table
        const userId = session?.user?.id;
        let dbRole = null;
        let dbRegistrationStep = null;

        const { data: dbUser, error: dbUserError } = await supabase
          ?.from('users')
          ?.select('role, registration_step')
          ?.eq('id', userId)
          ?.single();

        if (dbUserError) {
          console.error('⚠️ Could not read role from users table, falling back to professional flow:', dbUserError);
          // Fall through to professional flow as last resort
        } else {
          dbRole = dbUser?.role;
          dbRegistrationStep = dbUser?.registration_step;
        }

        // Admin/Superadmin flow - redirect to /admin
        if (dbRole === 'superadmin' || dbRole === 'admin') {
          console.log('✅ Admin user, redirecting to /admin');
          setIsProcessing(false);
          navigate('/admin', { replace: true });
          return;
        }

        // Client flow - simple redirect to dashboard after email confirmation
        if (dbRole === 'cliente') {
          console.log('✅ Client user confirmed, redirecting to client dashboard');

          const pendingRequest = session?.user?.user_metadata?.pending_request;
          if (pendingRequest && pendingRequest?.title && pendingRequest?.locationName) {
            try {
              const { data: inserted, error: insertError } = await supabase?.from('service_requests')?.insert([{
                  client_id: session?.user?.id,
                  service_type: pendingRequest?.serviceType,
                  title: pendingRequest?.title,
                  description: pendingRequest?.description,
                  location_mapbox_id: pendingRequest?.locationMapboxId,
                  location_name: pendingRequest?.locationName,
                  urgency: pendingRequest?.urgency || 'medium',
                  status: 'active',
                  assigned_professional_id: pendingRequest?.assignedProfessionalId || null,
                  source: pendingRequest?.source || 'professional_landing',
                  source_slug: pendingRequest?.sourceSlug || null,
                  preferred_date: pendingRequest?.preferredDate || null,
                  preferred_time_slot: pendingRequest?.preferredTimeSlot || null,
                  additional_details: pendingRequest?.additionalDetails || null,
                  created_at: new Date()?.toISOString(),
                }])?.select()?.single();

              if (!insertError && inserted?.id) {
                // Invoke match-request (non-blocking)
                supabase?.functions?.invoke('match-request', { body: { request_id: inserted?.id } })?.catch(e => console.error('match-request invoke failed:', e));

                // Clear pending_request from metadata
                await supabase?.auth?.updateUser({ data: { pending_request: null } });

                setIsProcessing(false);
                navigate('/cliente/dashboard?richiesta_inviata=1', { replace: true });
                return;
              } else {
                console.error('Failed to insert pending request:', insertError);
                // Fall through to normal dashboard redirect; the user can re-submit
              }
            } catch (e) {
              console.error('Error processing pending_request:', e);
              // Fall through to normal redirect
            }
          }

          setIsProcessing(false);
          navigate('/cliente/dashboard', { replace: true });
          return;
        }

        // Professional flow - check onboarding completion
        
        // CRITICAL FIX: Add delay to ensure RLS policies can evaluate properly
        // Session must be fully propagated before database queries
        await new Promise(resolve => setTimeout(resolve, 300));

        // Use registration_step from the DB query above if available,
        // otherwise re-query (handles the dbUserError fallback path)
        let registrationStep = dbRegistrationStep;

        if (registrationStep === null || registrationStep === undefined) {
          const { data: userDataFull, error: userError } = await supabase
            ?.from('users')
            ?.select('registration_step')
            ?.eq('id', userId)
            ?.single();

          if (userError) {
            console.error('❌ Error fetching user data:', userError);
            
            if (userError?.code === 'PGRST116' || userError?.message?.includes('no rows')) {
              console.log('⚠️ User row not found, redirecting to onboarding');
              setIsProcessing(false);
              navigate('/professional-registration-step-2', { replace: true });
              return;
            }
            
            console.log('⚠️ Database error, redirecting to onboarding');
            setIsProcessing(false);
            navigate('/professional-registration-step-2', { replace: true });
            return;
          }

          registrationStep = userDataFull?.registration_step;
        }

        console.log('✅ Registration step:', registrationStep);

        // Check professional onboarding status
        const step = registrationStep || 1;
        
        if (step === 1) {
          console.log('⚠️ Registration step 1, redirecting to step 2');
          setIsProcessing(false);
          navigate('/professional-registration-step-2', { replace: true });
        } else if (step >= 2) {
          console.log('✅ Registration complete (step >= 2), redirecting to professional dashboard');
          setIsProcessing(false);
          navigate('/professional-dashboard', { replace: true });
        } else {
          console.log('⚠️ Unknown registration state, redirecting to step 2');
          setIsProcessing(false);
          navigate('/professional-registration-step-2', { replace: true });
        }
      } catch (error) {
        console.error('❌ Callback error:', error);
        setIsProcessing(false);
        navigate('/login-form', { replace: true });
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Verifica in corso...</p>
      </div>
    </div>
  );
};

export default AuthCallback;